'use client';
import React, { useState, useEffect } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { MapPin, Building2, Clock, Zap, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { jobService, type JobListing } from '@/lib/services/jobService';

type SortKey = 'title' | 'company' | 'salaryMin' | 'postedDate' | 'openings';
type SortDir = 'asc' | 'desc';

export default function JobListingsTable() {
  const [allJobs, setAllJobs] = useState<JobListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('postedDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [regionFilter, setRegionFilter] = useState<'All' | 'SA' | 'International'>('All');

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const data = await jobService.getJobListings();
        setAllJobs(data);
      } catch (err: any) {
        setError(err.message ?? 'Failed to load job listings');
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filtered = allJobs.filter(
    (j) => regionFilter === 'All' || j.region === regionFilter
  );

  const sorted = [...filtered].sort((a, b) => {
    let aVal: string | number = (a as any)[sortKey] ?? '';
    let bVal: string | number = (b as any)[sortKey] ?? '';
    if (sortKey === 'postedDate') {
      const parseDate = (d: string) => {
        const [day, month, year] = d.split('/');
        return new Date(`${year}-${month}-${day}`).getTime();
      };
      aVal = parseDate(a.postedDate);
      bVal = parseDate(b.postedDate);
    }
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc'
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number);
  });

  const SortIcon = ({ col }: { col: SortKey }) =>
    sortKey === col ? (
      sortDir === 'asc' ? (
        <ChevronUp size={13} className="text-primary" />
      ) : (
        <ChevronDown size={13} className="text-primary" />
      )
    ) : (
      <ChevronDown size={13} className="text-muted-foreground opacity-40" />
    );

  const urgencyColor: Record<string, string> = {
    high: 'text-red-600 bg-red-50 border-red-200',
    medium: 'text-amber-600 bg-amber-50 border-amber-200',
    low: 'text-green-600 bg-green-50 border-green-200',
  };

  const saCount = allJobs.filter((j) => j.region === 'SA').length;
  const intlCount = allJobs.filter((j) => j.region === 'International').length;

  return (
    <div className="bg-card rounded-2xl card-shadow border border-border overflow-hidden">
      {/* Region filter tabs */}
      <div className="flex items-center gap-1.5 px-4 pt-3 pb-2.5 border-b border-border">
        {(['All', 'SA', 'International'] as const).map((r) => (
          <button
            key={r}
            onClick={() => setRegionFilter(r)}
            className={`px-3 py-1 rounded-full text-[11px] font-600 border transition-colors ${
              regionFilter === r
                ? 'bg-primary text-white border-primary' :'bg-secondary text-muted-foreground border-border hover:border-primary hover:text-primary'
            }`}
          >
            {r === 'All'
              ? `All Jobs (${allJobs.length})`
              : r === 'SA'
              ? `South Africa (${saCount})`
              : `International (${intlCount})`}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground text-sm">Loading job listings...</span>
          </div>
        ) : error ? (
          <div className="px-4 py-12 text-center">
            <p className="text-red-500 font-500 text-sm">{error}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60 border-b border-border">
                {[
                  { key: 'title' as SortKey, label: 'Job Title' },
                  { key: 'company' as SortKey, label: 'Company' },
                  { key: 'salaryMin' as SortKey, label: 'Salary' },
                  { key: 'openings' as SortKey, label: 'Openings' },
                  { key: 'postedDate' as SortKey, label: 'Posted' },
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    className="px-4 py-3 text-left font-600 text-muted-foreground cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort(key)}
                  >
                    <span className="flex items-center gap-1">
                      {label} <SortIcon col={key} />
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3 text-left font-600 text-muted-foreground">License</th>
                <th className="px-4 py-3 text-left font-600 text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left font-600 text-muted-foreground">Urgency</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-muted-foreground font-500">No job listings found.</p>
                  </td>
                </tr>
              ) : (
                sorted.map((job, idx) => (
                  <tr
                    key={job.id}
                    className={`border-b border-border last:border-0 hover:bg-secondary/30 transition-colors ${
                      idx % 2 === 0 ? '' : 'bg-secondary/10'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-600 text-foreground leading-snug">{job.title}</div>
                      <div className="flex items-center gap-1 mt-0.5 text-muted-foreground text-xs">
                        <MapPin size={11} />
                        {job.location}
                        {job.pdpRequired && (
                          <span className="ml-1 bg-blue-50 text-blue-600 border border-blue-200 text-[10px] font-600 px-1.5 py-0.5 rounded-full">
                            PDP
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-foreground font-500">
                        <Building2 size={13} className="text-muted-foreground flex-shrink-0" />
                        {job.company}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{job.jobType}</div>
                    </td>
                    <td className="px-4 py-3 font-600 text-foreground tabular-nums whitespace-nowrap">
                      {job.salaryDisplay}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-700 text-sm">
                        {job.openings}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Clock size={11} />
                        {job.postedDate}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-600 bg-secondary text-foreground px-2 py-1 rounded-md">
                        {job.licenseRequired}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Zap size={12} className="text-green-500 flex-shrink-0" />
                        <StatusBadge status="auto-applied" />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] font-700 uppercase tracking-wide px-2 py-1 rounded-full border ${urgencyColor[job.urgency]}`}
                      >
                        {job.urgency}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-5 py-3 border-t border-border bg-secondary/30 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Showing <span className="font-600 text-foreground">{sorted.length}</span> of{' '}
          <span className="font-600 text-foreground">{allJobs.length}</span> jobs
        </p>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Zap size={11} className="text-green-500" />
          Auto-applying to all matching vacancies
        </p>
      </div>
    </div>
  );
}